import React, { useState } from 'react';
import { X, Download, Music, RefreshCw, Check, AlertCircle, ExternalLink, Calendar, MapPin, Clock } from 'lucide-react';

const BandsintownImport = ({ isOpen, onClose, onImportShows, existingShows = {} }) => {
  const [artistName, setArtistName] = useState('');
  const [appId, setAppId] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState('input'); // 'input', 'preview', 'complete'

  // Check if a show already exists on a given date
  const showExistsOnDate = (dateKey) => {
    return existingShows[dateKey] && existingShows[dateKey].length > 0;
  };

  // Format date from Bandsintown format to our app format (YYYY-MM-DD)
  const formatDateKey = (datetime) => {
    const date = new Date(datetime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format time from datetime
  const formatTime = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Format display date
  const formatDisplayDate = (datetime) => {
    const date = new Date(datetime);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Fetch events from Bandsintown API
  const fetchEvents = async () => {
    if (!artistName.trim()) {
      setError('Please enter your artist/band name');
      return;
    }

    setLoading(true);
    setError('');
    setEvents([]);

    try {
      // Use a default app_id if none provided (Bandsintown allows this for basic usage)
      const applicationId = appId.trim() || 'tour_manager_app';
      const encodedArtist = encodeURIComponent(artistName.trim());
      const url = `https://rest.bandsintown.com/artists/${encodedArtist}/events?app_id=${applicationId}`;

      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Artist not found. Make sure the name matches exactly as it appears on Bandsintown.');
        }
        throw new Error(`Failed to fetch events (${response.status})`);
      }

      const data = await response.json();

      if (!data || data.length === 0) {
        setError('No upcoming events found for this artist on Bandsintown.');
        setLoading(false);
        return;
      }

      // Filter to only future events
      const now = new Date();
      const futureEvents = data.filter(event => new Date(event.datetime) >= now);

      if (futureEvents.length === 0) {
        setError('No upcoming events found. All events on Bandsintown are in the past.');
        setLoading(false);
        return;
      }

      // Mark events that already exist in calendar
      const eventsWithStatus = futureEvents.map(event => ({
        ...event,
        dateKey: formatDateKey(event.datetime),
        alreadyExists: showExistsOnDate(formatDateKey(event.datetime))
      }));

      setEvents(eventsWithStatus);
      
      // Pre-select events that don't already exist
      const newEventIds = eventsWithStatus
        .filter(e => !e.alreadyExists)
        .map(e => e.id);
      setSelectedEvents(newEventIds);
      
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Failed to fetch events from Bandsintown');
    } finally {
      setLoading(false);
    }
  };

  // Toggle event selection
  const toggleEventSelection = (eventId) => {
    setSelectedEvents(prev => 
      prev.includes(eventId)
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId]
    );
  };

  // Select/deselect all
  const toggleSelectAll = () => {
    const selectableEvents = events.filter(e => !e.alreadyExists);
    if (selectedEvents.length === selectableEvents.length) {
      setSelectedEvents([]);
    } else {
      setSelectedEvents(selectableEvents.map(e => e.id));
    }
  };

  // Convert Bandsintown event to our show format
  const convertToShow = (event) => {
    const venue = event.venue || {};
    const city = [venue.city, venue.region].filter(Boolean).join(', ');
    
    return {
      title: event.title || `${artistName} at ${venue.name || 'TBA'}`,
      venue: venue.name || '',
      city: city,
      loadInTime: '',
      showTime: formatTime(event.datetime),
      guarantee: '',
      cashMerch: '',
      venmoMerch: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      gas: '',
      hotel: '',
      food: '',
      tolls: '',
      otherExpenses: '',
      showNotes: event.description || '',
      setlist: '',
      crowdInfo: '',
      technicalNotes: '',
      nextTime: '',
      status: 'confirmed',
      bandsintownId: event.id,
      bandsintownUrl: event.url,
      ticketUrl: event.offers?.[0]?.url || ''
    };
  };

  // Import selected events
  const handleImport = () => {
    const eventsToImport = events.filter(e => selectedEvents.includes(e.id));
    
    const showsToAdd = eventsToImport.map(event => ({
      dateKey: event.dateKey,
      show: convertToShow(event)
    }));

    onImportShows(showsToAdd);
    setSuccess(`Successfully imported ${showsToAdd.length} show${showsToAdd.length !== 1 ? 's' : ''}!`);
    setStep('complete');
  };

  // Reset and close
  const handleClose = () => {
    setArtistName('');
    setAppId('');
    setEvents([]);
    setSelectedEvents([]);
    setError('');
    setSuccess('');
    setStep('input');
    onClose();
  };

  // Start over
  const handleStartOver = () => {
    setEvents([]);
    setSelectedEvents([]);
    setError('');
    setSuccess('');
    setStep('input');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-xl">
              <Music className="w-6 h-6 text-pink-600 dark:text-pink-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                Import from Bandsintown
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {step === 'input' && 'Enter your artist name to fetch shows'}
                {step === 'preview' && `${events.length} event${events.length !== 1 ? 's' : ''} found`}
                {step === 'complete' && 'Import complete!'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {/* Step 1: Input */}
          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Artist / Band Name *
                </label>
                <input
                  type="text"
                  value={artistName}
                  onChange={(e) => setArtistName(e.target.value)}
                  placeholder="Enter your name exactly as it appears on Bandsintown"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && fetchEvents()}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  API Key (Optional)
                </label>
                <input
                  type="text"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="Your Bandsintown API key"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Get your API key from{' '}
                  <a 
                    href="https://artists.bandsintown.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-pink-600 dark:text-pink-400 hover:underline"
                  >
                    Bandsintown for Artists
                  </a>
                  {' '}→ Settings → General
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300">
                  <AlertCircle size={20} />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Tips:</h4>
                <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• Use your exact artist name as it appears on Bandsintown</li>
                  <li>• Make sure you have events listed on Bandsintown first</li>
                  <li>• Only future events will be imported</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Select All */}
              <div className="flex items-center justify-between">
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-pink-600 dark:text-pink-400 hover:underline font-medium"
                >
                  {selectedEvents.length === events.filter(e => !e.alreadyExists).length 
                    ? 'Deselect All' 
                    : 'Select All New'}
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedEvents.length} selected
                </span>
              </div>

              {/* Events List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {events.map(event => (
                  <div
                    key={event.id}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      event.alreadyExists
                        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                        : selectedEvents.includes(event.id)
                          ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div className="pt-1">
                        {event.alreadyExists ? (
                          <div className="w-5 h-5 rounded bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <Check size={14} className="text-gray-500" />
                          </div>
                        ) : (
                          <button
                            onClick={() => toggleEventSelection(event.id)}
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              selectedEvents.includes(event.id)
                                ? 'bg-pink-500 border-pink-500'
                                : 'border-gray-300 dark:border-gray-600 hover:border-pink-400'
                            }`}
                          >
                            {selectedEvents.includes(event.id) && (
                              <Check size={14} className="text-white" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Event Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                            {event.venue?.name || 'Venue TBA'}
                          </span>
                          {event.alreadyExists && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full whitespace-nowrap">
                              Already in calendar
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDisplayDate(event.datetime)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {formatTime(event.datetime)}
                          </span>
                          {event.venue?.city && (
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {[event.venue.city, event.venue.region].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>

                        {event.url && (
                          <a
                            href={event.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-2 text-xs text-pink-600 dark:text-pink-400 hover:underline"
                          >
                            <ExternalLink size={12} />
                            View on Bandsintown
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                {success}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your shows have been added to the calendar. You can now view and edit them.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 md:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          {step === 'input' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={fetchEvents}
                disabled={loading || !artistName.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Fetch Events
                  </>
                )}
              </button>
            </>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={handleStartOver}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors"
              >
                ← Back
              </button>
              <button
                onClick={handleImport}
                disabled={selectedEvents.length === 0}
                className="flex items-center gap-2 px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Download size={18} />
                Import {selectedEvents.length} Show{selectedEvents.length !== 1 ? 's' : ''}
              </button>
            </>
          )}

          {step === 'complete' && (
            <>
              <button
                onClick={handleStartOver}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors"
              >
                <RefreshCw size={18} />
                Import More
              </button>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-pink-600 text-white rounded-xl hover:bg-pink-700 transition-colors"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BandsintownImport;